import { useState, useEffect } from 'react';
import { Collapse } from '@material-ui/core';
import {
  DevHubBox,
  DevHubCard,
  DevHubTypography,
  DevHubGrid,
  DevHubTooltip,
  DevHubDialog,
  DevHubDialogTitle,
  DevHubDialogContent,
  DevHubDialogActions,
  DevHubCircularProgress,
  DevHubIconButton,
  DevHubButton,
  DevHubDynamicIcon,
  devhubMakeStyles,
} from '@devxdiscover/devhub-ui';
import { ExperimentalApplication } from './types';
import {
  discoveryApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { useProvisioningAPI } from './onboarding-modal/hooks/useProvisioningAPI';
import { useProvisioningState } from './onboarding-modal/hooks/useProvisioningState';
import { useSignalHandling } from './onboarding-modal/hooks/useSignalHandling';
import { useDeletionSignalTracking } from './onboarding-modal/hooks/useDeletionSignalTracking';
import {
  GITHUB_ORG,
  formatGitHubRepoName,
  formatCnapTenancyName,
} from './utils/resourceFormatting';
import {
  ResourceLabel,
  ResourceItem,
  ResourceItemWithRetry,
  IdeaMetadata,
  FailedDeletionCard,
} from './components';
import type { ResourceProvisioningStatus } from './components/ResourceItemWithRetry';
import { deletionStateManager } from './deletionStateManager';

const useStyles = devhubMakeStyles((theme: any) => ({
  adGroupsBox: {
    display: 'flex',
    flexDirection: 'column',
    padding: '8px',
    marginBottom: '8px',
    borderRadius: 4,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.devhubBackground.workbox,
  },
  adGroupsCollapseInner: {
    paddingTop: '8px',
    paddingLeft: '28px',
    borderTop: `1px solid ${theme.palette.divider}`,
    marginTop: '8px',
  },
  resourceLink: {
    fontFamily: 'monospace',
    fontSize: '0.8rem',
    color: theme.palette.primary.main,
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  resourceSvgIcon: {
    marginRight: theme.spacing(1.5),
    width: 20,
    height: 20,
    display: 'inline-block',
  },
  expandIcon: {
    marginLeft: 'auto',
    transition: 'transform 0.3s',
  },
  expandIconRotated: {
    transform: 'rotate(180deg)',
  },
  deletingOverlay: {
    position: 'relative',
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(255, 255, 255, 0.7)',
      zIndex: 1,
      pointerEvents: 'none',
    },
  },
  deletingSpinner: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    zIndex: 2,
  },
}));

interface IdeaCardProps {
  idea: ExperimentalApplication;
  onDelete?: (applicationId: number) => void;
  onRefresh?: (applicationId: number) => void;
  refreshing?: boolean;
  showActions?: boolean;
  size?: 'small' | 'medium';
}

/**
 * Card component for displaying a single experimental application idea with its provisioned resources
 * @param props - Component props
 * @param props.idea - Experimental application to display
 * @param props.onDelete - Optional callback when application is deleted
 * @param props.onRefresh - Optional callback when application is refreshed
 * @param props.refreshing - Whether the card is currently refreshing
 * @param props.showActions - Whether to show action buttons (default: true)
 * @param props.size - Size variant for text scaling (default: medium)
 */
export const IdeaCard = ({
  idea,
  onDelete,
  onRefresh,
  refreshing = false,
  showActions = true,
  size = 'medium',
}: IdeaCardProps) => {
  const classes = useStyles();
  const [adGroupsExpanded, setAdGroupsExpanded] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [refreshTooltipOpen, setRefreshTooltipOpen] = useState(false);
  const [_deletionStateVersion, setDeletionStateVersion] = useState(0);
  const [retryingResource, setRetryingResource] = useState<
    'github' | 'cnap' | 'adGroups' | null
  >(null);
  const discoveryApi = useApi(discoveryApiRef);
  const identityApi = useApi(identityApiRef);

  const provisioningHook = useProvisioningState();

  // Set up signal handling to receive provisioning updates during retry
  const { setCurrentTaskId } = useSignalHandling(
    provisioningHook.updateProvisioningComponent,
  );

  // Track deletion state via signals for this specific app
  useDeletionSignalTracking(idea.applicationId);

  const { retryProvisioning } = useProvisioningAPI(
    provisioningHook.updateProvisioningComponent,
    provisioningHook.setMutationError,
    setCurrentTaskId,
    provisioningHook.provisioningComponents,
  );

  // Get current deletion state for this app
  const deletionState = deletionStateManager.getDeletionState(
    idea.applicationId,
  );

  // eslint-disable-next-line no-console
  console.log(
    '[IdeaCard] Deletion state for app',
    idea.applicationId,
    ':',
    deletionState,
  );

  // Set up timeout for deletion - if it takes too long, trigger a refresh
  useEffect(() => {
    if (!deletionState || deletionState.status !== 'in-progress') {
      return;
    }

    // 30 second timeout for deletion
    const timeoutId = setTimeout(() => {
      // eslint-disable-next-line no-console
      console.log(
        '[IdeaCard] Deletion timeout reached for app',
        idea.applicationId,
        '- triggering refresh',
      );
      setDeleting(false);
      if (onRefresh) {
        onRefresh(idea.applicationId);
      }
    }, 30000); // 30 seconds

    // eslint-disable-next-line consistent-return
    return () => clearTimeout(timeoutId);
  }, [deletionState, idea.applicationId, onRefresh]);

  // Listen for deletion state changes and force re-render
  useEffect(() => {
    const handleDeletionStateChange = () => {
      setDeletionStateVersion(v => v + 1);
      // Clear deleting spinner when state changes
      setDeleting(false);
    };
    window.addEventListener(
      'deletion-state-changed',
      handleDeletionStateChange,
    );
    return () => {
      window.removeEventListener(
        'deletion-state-changed',
        handleDeletionStateChange,
      );
    };
  }, []);

  // Reset AD Groups expansion when idea changes
  useEffect(() => {
    setAdGroupsExpanded(false);
  }, [idea.applicationId]);

  // Monitor provisioning components to stop retry spinner when resource completes
  useEffect(() => {
    if (!retryingResource) return;

    // Map retry resource type to provisioning component ID
    const componentMap = {
      github: 'github-repo',
      cnap: 'cnap-tenancy',
      adGroups: 'ad-groups',
    };

    const componentId = componentMap[retryingResource];
    const component = provisioningHook.provisioningComponents.find(
      c => c.id === componentId,
    );

    // Stop spinner when resource reaches terminal state
    if (
      component &&
      (component.status === 'completed' ||
        component.status === 'failed' ||
        component.status === 'skipped')
    ) {
      setRetryingResource(null);

      // Refresh card to show updated state from backend
      if (onRefresh && component.status === 'completed') {
        onRefresh(idea.applicationId);
      }
    }
  }, [
    provisioningHook.provisioningComponents,
    retryingResource,
    onRefresh,
    idea.applicationId,
  ]);

  const toggleAdGroups = () => {
    setAdGroupsExpanded(prev => !prev);
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleRefresh = () => {
    setRefreshTooltipOpen(false);
    if (onRefresh) {
      onRefresh(idea.applicationId);
    }
  };

  const handleDeleteConfirm = async () => {
    // Mark as deleting in session storage BEFORE making API call
    deletionStateManager.markAsDeleting(idea.applicationId);

    setDeleting(true);
    setDeleteDialogOpen(false); // Close dialog immediately

    try {
      const baseUrl = await discoveryApi.getBaseUrl('experimental-apps');
      const response = await fetch(baseUrl, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          applicationId: idea.applicationId,
          name: idea.name,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to delete idea: ${response.statusText}`);
      }

      // Call the parent's onDelete callback to refresh the list
      // Deletion state will be cleared by signal handler on success
      if (onDelete) {
        onDelete(idea.applicationId);
      }
    } catch (error) {
      // Deletion state will be marked as failed by signal handler
      // eslint-disable-next-line no-console
      console.error('Error deleting idea:', error);
      // Reset deleting state if API call itself fails (not resource deletion)
      setDeleting(false);
      deletionStateManager.markAsFailed(idea.applicationId, [], {
        api: error instanceof Error ? error.message : String(error),
      });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  // Handle resuming a failed deletion
  const handleResumeDeletion = async () => {
    // Re-initiate delete flow - signals will track progress
    await handleDeleteConfirm();
  };

  // Handle cancelling a failed deletion (keep the app)
  const handleCancelDeletion = () => {
    deletionStateManager.clearDeletion(idea.applicationId);
    // Reset tooltip state to avoid lingering tooltips
    setRefreshTooltipOpen(false);
    // Force re-render to show normal card UI
    setDeletionStateVersion(v => v + 1);
  };

  // Handle retry for individual resources
  // Uses the same provisioning code path as the modal retry
  const handleRetryResource = async (
    resourceType: 'github' | 'cnap' | 'adGroups',
  ) => {
    setRetryingResource(resourceType);
    provisioningHook.resetProvisioningForRetry();

    try {
      const identity = await identityApi.getBackstageIdentity();
      const userLogin = identity.userEntityRef.split('/')[1];

      // Use the same provisioning API as modal retry
      // It will skip already-completed resources and retry failed ones
      // This starts provisioning - the useEffect above will monitor signals
      // and stop the spinner when the resource reaches a terminal state
      await retryProvisioning(idea.name, idea.description || '', userLogin);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`Error retrying ${resourceType}:`, error);
      // Error handling is done in useProvisioningAPI hook
      setRetryingResource(null);
    }
  };

  // Extract resources by type
  const adGroups = (idea.resources.adGroups || []).sort((a, b) =>
    a.name.localeCompare(b.name),
  );
  const github = idea.resources.github;
  const githubRepos = idea.resources.githubRepos || [];
  const cnap = idea.resources.cnap;

  // Determine resource provisioning status based on presence
  // Extract provisioning component state for error messages during retry
  const githubComponent = provisioningHook.provisioningComponents.find(
    c => c.id === 'github-repo',
  );
  const cnapComponent = provisioningHook.provisioningComponents.find(
    c => c.id === 'cnap-tenancy',
  );
  const adGroupsComponent = provisioningHook.provisioningComponents.find(
    c => c.id === 'ad-groups',
  );

  let githubStatus: ResourceProvisioningStatus;
  let githubError: string | undefined;
  if (refreshing && retryingResource === 'github') {
    githubStatus = 'in-progress';
  } else if (github || githubRepos.length > 0) {
    githubStatus = 'completed';
  } else {
    githubStatus = 'missing';
  }
  // Extract error from provisioning component if available (even after retry completes)
  if (githubComponent?.error) {
    githubError = githubComponent.error;
  }

  let cnapStatus: ResourceProvisioningStatus;
  let cnapError: string | undefined;
  if (refreshing && retryingResource === 'cnap') {
    cnapStatus = 'in-progress';
  } else if (cnap) {
    cnapStatus = 'completed';
  } else {
    cnapStatus = 'missing';
  }
  // Extract error from provisioning component if available (even after retry completes)
  if (cnapComponent?.error) {
    cnapError = cnapComponent.error;
  }

  let adGroupsStatus: ResourceProvisioningStatus;
  let adGroupsError: string | undefined;
  if (refreshing && retryingResource === 'adGroups') {
    adGroupsStatus = 'in-progress';
  } else if (adGroups.length > 0) {
    adGroupsStatus = 'completed';
  } else {
    adGroupsStatus = 'missing';
  }
  // Extract error from provisioning component if available (even after retry completes)
  if (adGroupsComponent?.error) {
    adGroupsError = adGroupsComponent.error;
  }

  // eslint-disable-next-line no-console
  console.log(
    'IdeaCard for app',
    idea.applicationId,
    '- GitHub repos:',
    githubRepos,
  );

  // Check if this is a failed deletion - show special UI
  if (deletionState?.status === 'failed') {
    return (
      <FailedDeletionCard
        idea={idea}
        failedResources={deletionState.failedResources}
        errors={deletionState.errors}
        onResume={handleResumeDeletion}
        onCancel={handleCancelDeletion}
        resuming={deleting}
      />
    );
  }

  // Check if deletion is in progress
  const isDeletingInProgress = deletionState?.status === 'in-progress';

  return (
    <>
      <DevHubBox
        position="relative"
        className={isDeletingInProgress ? classes.deletingOverlay : undefined}
      >
        {isDeletingInProgress && (
          <div className={classes.deletingSpinner}>
            <DevHubCircularProgress size={60} />
          </div>
        )}
        <DevHubCard
          title={idea.name}
          actionItem={
            showActions ? (
              <DevHubBox display="flex" alignItems="center" pt={1} pr={1}>
                {onRefresh && (
                  <DevHubTooltip
                    title="Refresh resources"
                    open={refreshTooltipOpen && !refreshing}
                    onOpen={() => setRefreshTooltipOpen(true)}
                    onClose={() => setRefreshTooltipOpen(false)}
                  >
                    <span>
                      <DevHubIconButton
                        size="small"
                        onClick={handleRefresh}
                        disabled={refreshing}
                        aria-label="Refresh resources"
                      >
                        {refreshing ? (
                          <DevHubCircularProgress size={20} />
                        ) : (
                          <DevHubDynamicIcon iconName="Refresh" />
                        )}
                      </DevHubIconButton>
                    </span>
                  </DevHubTooltip>
                )}
                <DevHubTooltip title="Delete idea">
                  <DevHubIconButton
                    size="small"
                    onClick={handleDeleteClick}
                    aria-label="Delete idea"
                  >
                    <DevHubDynamicIcon iconName="Delete" />
                  </DevHubIconButton>
                </DevHubTooltip>
              </DevHubBox>
            ) : undefined
          }
          body={
            <DevHubBox padding={size === 'small' ? 'small' : 'medium'}>
              {idea.description && (
                <DevHubTypography
                  variant={size === 'small' ? 'body2' : 'body1'}
                  color="textSecondary"
                  style={{ marginBottom: '16px' }}
                >
                  {idea.description}
                </DevHubTypography>
              )}
              <IdeaMetadata
                applicationId={idea.applicationId}
                ownerText={`${idea.owner.firstName} ${idea.owner.lastName}`}
                variant={size === 'small' ? 'small' : 'medium'}
              />
              <DevHubTypography
                variant={size === 'small' ? 'caption' : 'body2'}
                color="textSecondary"
                style={{
                  marginBottom: '16px',
                  display: 'block',
                }}
              >
                Created: {new Date(idea.createdAt).toLocaleDateString()}
              </DevHubTypography>

              <div style={{ marginTop: 16 }}>
                <DevHubTypography
                  variant="subtitle2"
                  style={{ marginBottom: '8px', fontWeight: 600 }}
                >
                  Resources
                </DevHubTypography>

                <DevHubGrid container spacing={1}>
                  {/* GitHub Repository */}
                  <DevHubGrid item xs={12}>
                    <ResourceLabel type="github">
                      GitHub Repository
                    </ResourceLabel>
                    {githubStatus === 'completed' ? (
                      <ResourceItem
                        iconSrc="/icons/github.svg"
                        iconAlt="GitHub"
                        href={github?.location || githubRepos[0]?.location}
                      >
                        {github?.slug ||
                          githubRepos[0]?.slug ||
                          `${GITHUB_ORG}/${github?.name || githubRepos[0]?.name}`}
                      </ResourceItem>
                    ) : (
                      <ResourceItemWithRetry
                        iconSrc="/icons/github.svg"
                        iconAlt="GitHub"
                        resourceName={formatGitHubRepoName(idea.name)}
                        status={githubStatus}
                        progress={refreshing ? 50 : 0}
                        error={githubError}
                        onRetry={() => handleRetryResource('github')}
                        retrying={retryingResource === 'github'}
                      />
                    )}
                  </DevHubGrid>

                  {/* CNAP Tenancy */}
                  <DevHubGrid item xs={12}>
                    <ResourceLabel type="cnap">CNAP Tenancy</ResourceLabel>
                    {cnapStatus === 'completed' ? (
                      <ResourceItem
                        iconSrc="/icons/cnap.svg"
                        iconAlt="CNAP"
                        extra={
                          <>
                            <span
                              style={{
                                marginLeft: '8px',
                                marginRight: '4px',
                                opacity: 0.4,
                              }}
                            >
                              •
                            </span>
                            <a
                              href={cnap?.location}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={classes.resourceLink}
                            >
                              docs
                            </a>
                          </>
                        }
                      >
                        {cnap?.name}
                      </ResourceItem>
                    ) : (
                      <ResourceItemWithRetry
                        iconSrc="/icons/cnap.svg"
                        iconAlt="CNAP"
                        resourceName={formatCnapTenancyName(idea.name)}
                        status={cnapStatus}
                        progress={refreshing ? 50 : 0}
                        error={cnapError}
                        onRetry={() => handleRetryResource('cnap')}
                        retrying={retryingResource === 'cnap'}
                      />
                    )}
                  </DevHubGrid>

                  {/* AD Groups */}
                  <DevHubGrid item xs={12}>
                    <ResourceLabel type="adGroups">AD Groups</ResourceLabel>
                    {adGroupsStatus === 'completed' ? (
                      <div className={classes.adGroupsBox}>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            width: '100%',
                            cursor: 'pointer',
                          }}
                          onClick={toggleAdGroups}
                          onKeyPress={e => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              toggleAdGroups();
                            }
                          }}
                          role="button"
                          tabIndex={0}
                        >
                          <img
                            src="/icons/azure.svg"
                            alt="AD Groups"
                            className={classes.resourceSvgIcon}
                          />
                          <span style={{ fontSize: '0.8rem' }}>
                            AD Groups ({adGroups.length})
                          </span>
                          <DevHubDynamicIcon
                            iconName="ExpandMore"
                            className={`${classes.expandIcon} ${
                              adGroupsExpanded ? classes.expandIconRotated : ''
                            }`}
                          />
                        </div>
                        <Collapse in={adGroupsExpanded}>
                          <div className={classes.adGroupsCollapseInner}>
                            {adGroups.map((group, index) => (
                              <div
                                key={index}
                                style={{
                                  marginBottom:
                                    index < adGroups.length - 1 ? '8px' : 0,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                <a
                                  href={group.location}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className={classes.resourceLink}
                                  style={{
                                    display: 'block',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {group.name}
                                </a>
                              </div>
                            ))}
                          </div>
                        </Collapse>
                      </div>
                    ) : (
                      <ResourceItemWithRetry
                        iconSrc="/icons/azure.svg"
                        iconAlt="AD Groups"
                        resourceName="AD Groups (3)"
                        status={adGroupsStatus}
                        progress={refreshing ? 50 : 0}
                        error={adGroupsError}
                        onRetry={() => handleRetryResource('adGroups')}
                        retrying={retryingResource === 'adGroups'}
                      />
                    )}
                  </DevHubGrid>
                </DevHubGrid>
              </div>
            </DevHubBox>
          }
        />

        {/* Delete Confirmation Dialog */}
        <DevHubDialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
          <DevHubDialogTitle>Delete Idea</DevHubDialogTitle>
          <DevHubDialogContent>
            <DevHubTypography>
              Are you sure you want to delete '{idea.name}'?
            </DevHubTypography>
            <DevHubTypography
              style={{
                marginTop: '12px',
                marginBottom: '12px',
                fontWeight: 600,
              }}
            >
              This will remove the following resources:
            </DevHubTypography>

            {/* GitHub Repository */}
            {githubStatus === 'completed' && (
              <DevHubBox display="flex" alignItems="center" mb={2}>
                <DevHubTooltip title="GitHub Repository" placement="left">
                  <img
                    src="/icons/github.svg"
                    alt="GitHub"
                    style={{
                      width: '20px',
                      height: '20px',
                      marginRight: '10px',
                    }}
                  />
                </DevHubTooltip>
                <DevHubBox>
                  <DevHubTypography
                    variant="body2"
                    style={{
                      fontFamily: 'monospace',
                      fontSize: '0.9rem',
                      color: '#1976d2',
                    }}
                  >
                    {github?.slug ||
                      githubRepos[0]?.slug ||
                      `${GITHUB_ORG}/${formatGitHubRepoName(idea.name)}`}
                  </DevHubTypography>
                  <DevHubTypography
                    variant="caption"
                    style={{
                      color: '#ff9800',
                      fontSize: '0.75rem',
                      fontStyle: 'italic',
                    }}
                  >
                    will be archived
                  </DevHubTypography>
                </DevHubBox>
              </DevHubBox>
            )}

            {/* CNAP Tenancy */}
            {cnapStatus === 'completed' && (
              <DevHubBox display="flex" alignItems="center" mb={2}>
                <DevHubTooltip title="CNAP Tenancy" placement="left">
                  <img
                    src="/icons/cnap.svg"
                    alt="CNAP"
                    style={{
                      width: '20px',
                      height: '20px',
                      marginRight: '10px',
                    }}
                  />
                </DevHubTooltip>
                <DevHubBox>
                  <DevHubTypography
                    variant="body2"
                    style={{
                      fontFamily: 'monospace',
                      fontSize: '0.9rem',
                      color: '#1976d2',
                    }}
                  >
                    {cnap?.name}
                  </DevHubTypography>
                  <DevHubTypography
                    variant="caption"
                    style={{
                      color: '#d32f2f',
                      fontSize: '0.75rem',
                      fontStyle: 'italic',
                    }}
                  >
                    will be deleted
                  </DevHubTypography>
                </DevHubBox>
              </DevHubBox>
            )}

            {/* Active Directory Groups */}
            {adGroupsStatus === 'completed' && (
              <DevHubBox display="flex" mb={2}>
                <DevHubTooltip title="Active Directory Groups" placement="left">
                  <img
                    src="/icons/azure.svg"
                    alt="Azure AD"
                    style={{
                      width: '20px',
                      height: '20px',
                      marginRight: '10px',
                      marginTop: '2px',
                    }}
                  />
                </DevHubTooltip>
                <DevHubBox>
                  {adGroups.map((group, index) => (
                    <DevHubTypography
                      key={index}
                      variant="body2"
                      style={{
                        fontFamily: 'monospace',
                        fontSize: '0.9rem',
                        color: '#1976d2',
                      }}
                    >
                      {group.name}
                    </DevHubTypography>
                  ))}
                  <DevHubTypography
                    variant="caption"
                    style={{
                      color: '#d32f2f',
                      fontSize: '0.75rem',
                      fontStyle: 'italic',
                      marginTop: '4px',
                      display: 'block',
                    }}
                  >
                    will be deleted
                  </DevHubTypography>
                </DevHubBox>
              </DevHubBox>
            )}

            <DevHubTypography
              style={{
                marginTop: '16px',
                color: '#d32f2f',
                fontWeight: 'bold',
              }}
            >
              This action cannot be undone.
            </DevHubTypography>
          </DevHubDialogContent>
          <DevHubDialogActions>
            <DevHubButton onClick={handleDeleteCancel} disabled={deleting}>
              Cancel
            </DevHubButton>
            <DevHubButton
              onClick={handleDeleteConfirm}
              color="secondary"
              disabled={deleting}
              startIcon={
                deleting ? (
                  <DevHubCircularProgress size={16} />
                ) : (
                  <DevHubDynamicIcon iconName="Delete" />
                )
              }
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </DevHubButton>
          </DevHubDialogActions>
        </DevHubDialog>
      </DevHubBox>
    </>
  );
};
